import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { UserPlus, MagnifyingGlass, Warning, CheckCircle, Plus } from '@phosphor-icons/react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../../utils/api';

/**
 * AdminAddPlayerDialog - Dialog for admin to manually add a player to a session
 * Supports both searching existing players AND creating new players
 */
export function AdminAddPlayerDialog({
  open,
  onOpenChange,
  session,
  onSuccess
}) {
  const [mode, setMode] = useState('search'); // 'search' or 'create'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // New player form data
  const [newPlayerData, setNewPlayerData] = useState({
    name: '',
    date_of_birth: '',
    grade: '',
    instagram_handle: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    parent_name: '',
    parent_email: '',
    parent_phone: ''
  });

  // Search for players when query changes
  useEffect(() => {
    const searchPlayers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/admin/players/search?q=${encodeURIComponent(searchQuery)}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchPlayers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setMode('search');
      setSearchQuery('');
      setSearchResults([]);
      setSelectedPlayer(null);
      setPaymentStatus('pending');
      setNotes('');
      setNewPlayerData({
        name: '',
        date_of_birth: '',
        grade: '',
        instagram_handle: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        parent_name: '',
        parent_email: '',
        parent_phone: ''
      });
    }
  }, [open]);

  const handleCreateAndAdd = async () => {
    // Validate required fields
    if (!newPlayerData.name || !newPlayerData.date_of_birth) {
      toast.error('Player name and date of birth are required');
      return;
    }
    if (!newPlayerData.parent_name || !newPlayerData.parent_email) {
      toast.error('Parent name and email are required');
      return;
    }

    setSubmitting(true);
    try {
      // Create player via admin endpoint
      const response = await fetch(`${getApiBaseUrl()}/api/admin/players/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newPlayerData,
          grade: newPlayerData.grade ? parseInt(newPlayerData.grade) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Now add to session
        const regResponse = await fetch(`${getApiBaseUrl()}/api/admin/registrations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            session_id: session.session_id,
            player_id: data.player_id,
            payment_status: paymentStatus,
            notes: notes || 'Admin created player'
          })
        });

        if (regResponse.ok) {
          toast.success(`Created ${newPlayerData.name} and added to session`);
          onSuccess?.();
          onOpenChange(false);
        } else {
          const error = await regResponse.json();
          toast.error(error.detail || 'Failed to add to session');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create player');
      }
    } catch (error) {
      console.error('Create player error:', error);
      toast.error('Failed to create player');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlayer) {
      toast.error('Please select a player');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: session.session_id,
          player_id: selectedPlayer.player_id,
          payment_status: paymentStatus,
          notes: notes || null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || `Added ${selectedPlayer.name} to session`);
        if (data.warning) {
          toast.warning(data.warning);
        }
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error(data.detail || 'Failed to add player');
      }
    } catch (error) {
      toast.error('Failed to add player to session');
    } finally {
      setSubmitting(false);
    }
  };

  const getGradeDisplay = (player) => {
    if (player.grade) {
      const suffix = player.grade === 3 ? 'rd' : player.grade === 2 ? 'nd' : player.grade === 1 ? 'st' : 'th';
      return `${player.grade}${suffix} Grade`;
    }
    return player.grade_group ? 
      (player.grade_group === 'elementary' ? 'Elementary' : 
       player.grade_group === 'middle' ? 'Middle School' : 'High School') 
      : `Age ${player.age}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus size={20} weight="duotone" />
            ADD PLAYER TO SESSION
          </DialogTitle>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex gap-2 p-1 bg-muted/30 rounded-lg">
          <Button
            type="button"
            variant={mode === 'search' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setMode('search')}
            data-testid="find-existing-tab"
          >
            <MagnifyingGlass size={16} className="mr-1" />
            Find Existing
          </Button>
          <Button
            type="button"
            variant={mode === 'create' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setMode('create')}
            data-testid="create-new-tab"
          >
            <Plus size={16} className="mr-1" />
            Create New
          </Button>
        </div>

        {mode === 'search' ? (
          <div className="space-y-4">
            {/* Search for player */}
            <div>
              <Label>Search Player</Label>
              <div className="relative mt-1.5">
                <MagnifyingGlass 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                />
                <Input
                  placeholder="Type player name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                data-testid="player-search-input"
              />
            </div>
            
            {/* Search Results */}
            {searching && (
              <div className="mt-2 p-3 text-center text-muted-foreground">
                <LoadingSpinner size="sm" />
              </div>
            )}
            
            {!searching && searchResults.length > 0 && !selectedPlayer && (
              <div className="mt-2 border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                {searchResults.map((player) => (
                  <button
                    key={player.player_id}
                    onClick={() => {
                      setSelectedPlayer(player);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="w-full p-3 text-left hover:bg-muted/50 transition-colors"
                    data-testid={`player-result-${player.player_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{player.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {getGradeDisplay(player)} • {player.parent_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {player.instagram_handle && player.instagram_handle !== 'n/a' && (
                          <span className="text-xs text-pink-400">{player.instagram_handle}</span>
                        )}
                        {player.has_valid_waiver ? (
                          <CheckCircle size={16} className="text-green-500" weight="fill" />
                        ) : (
                          <Warning size={16} className="text-yellow-500" weight="fill" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Player */}
          {selectedPlayer && (
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-primary">{selectedPlayer.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {getGradeDisplay(selectedPlayer)} • {selectedPlayer.parent_email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPlayer(null)}
                >
                  Change
                </Button>
              </div>
              {!selectedPlayer.has_valid_waiver && (
                <p className="mt-2 text-xs text-yellow-500 flex items-center gap-1">
                  <Warning size={14} /> No valid waiver on file
                </p>
              )}
            </div>
          )}

          {/* Payment Status */}
          <div>
            <Label>Payment Status</Label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger className="mt-1.5" data-testid="payment-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Paid (Cash/Venmo/etc.)</SelectItem>
                <SelectItem value="comp">Comp/Free (No charge)</SelectItem>
                <SelectItem value="invoice">Invoice Later</SelectItem>
                <SelectItem value="pending">Pending (Parent pays online)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {paymentStatus === 'paid' && 'Player has already paid - spot confirmed'}
              {paymentStatus === 'comp' && 'Free session - no payment required'}
              {paymentStatus === 'invoice' && 'Will be billed later - spot confirmed'}
              {paymentStatus === 'pending' && 'Parent will receive payment link'}
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              placeholder="Add any notes about this registration..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5"
              rows={2}
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!selectedPlayer || submitting}
            className="w-full"
            data-testid="add-player-submit"
          >
            {submitting ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : (
              <UserPlus size={18} className="mr-2" />
            )}
            Add Player to Session
          </Button>
        </div>
        ) : (
          /* CREATE NEW PLAYER MODE */
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create a new player and add them directly to this session.
            </p>
            
            {/* Player Info */}
            <div className="border-b border-border pb-4">
              <p className="text-sm font-medium mb-3">Player Information</p>
              <div className="space-y-3">
                <div>
                  <Label>Player Name *</Label>
                  <Input
                    placeholder="Full name"
                    value={newPlayerData.name}
                    onChange={(e) => setNewPlayerData({...newPlayerData, name: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-name"
                  />
                </div>
                <div>
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={newPlayerData.date_of_birth}
                    onChange={(e) => setNewPlayerData({...newPlayerData, date_of_birth: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-dob"
                  />
                </div>
                <div>
                  <Label>Grade Level</Label>
                  <Select
                    value={newPlayerData.grade}
                    onValueChange={(value) => setNewPlayerData({...newPlayerData, grade: value})}
                  >
                    <SelectTrigger className="mt-1" data-testid="new-player-grade">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3rd Grade</SelectItem>
                      <SelectItem value="4">4th Grade</SelectItem>
                      <SelectItem value="5">5th Grade</SelectItem>
                      <SelectItem value="6">6th Grade</SelectItem>
                      <SelectItem value="7">7th Grade</SelectItem>
                      <SelectItem value="8">8th Grade</SelectItem>
                      <SelectItem value="9">9th Grade</SelectItem>
                      <SelectItem value="10">10th Grade</SelectItem>
                      <SelectItem value="11">11th Grade</SelectItem>
                      <SelectItem value="12">12th Grade</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Instagram Handle</Label>
                  <Input
                    placeholder="@handle (optional)"
                    value={newPlayerData.instagram_handle}
                    onChange={(e) => setNewPlayerData({...newPlayerData, instagram_handle: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-instagram"
                  />
                </div>
              </div>
            </div>
            
            {/* Parent Info */}
            <div className="border-b border-border pb-4">
              <p className="text-sm font-medium mb-3">Parent/Guardian Information</p>
              <div className="space-y-3">
                <div>
                  <Label>Parent Name *</Label>
                  <Input
                    placeholder="Full name"
                    value={newPlayerData.parent_name}
                    onChange={(e) => setNewPlayerData({...newPlayerData, parent_name: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-parent-name"
                  />
                </div>
                <div>
                  <Label>Parent Email *</Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newPlayerData.parent_email}
                    onChange={(e) => setNewPlayerData({...newPlayerData, parent_email: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-parent-email"
                  />
                </div>
                <div>
                  <Label>Parent Phone</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newPlayerData.parent_phone}
                    onChange={(e) => setNewPlayerData({...newPlayerData, parent_phone: e.target.value})}
                    className="mt-1"
                    data-testid="new-player-parent-phone"
                  />
                    onChange={(e) => setNewPlayerData({...newPlayerData, parent_phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Emergency Contact */}
            <div className="border-b border-border pb-4">
              <p className="text-sm font-medium mb-3">Emergency Contact</p>
              <div className="space-y-3">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    placeholder="Full name"
                    value={newPlayerData.emergency_contact_name}
                    onChange={(e) => setNewPlayerData({...newPlayerData, emergency_contact_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newPlayerData.emergency_contact_phone}
                    onChange={(e) => setNewPlayerData({...newPlayerData, emergency_contact_phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Payment Status */}
            <div>
              <Label>Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid (Cash/Venmo/etc.)</SelectItem>
                  <SelectItem value="comp">Comp/Free (No charge)</SelectItem>
                  <SelectItem value="invoice">Invoice Later</SelectItem>
                  <SelectItem value="pending">Pending (Parent pays online)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
            
            {/* Submit Button */}
            <Button
              onClick={handleCreateAndAdd}
              disabled={submitting || !newPlayerData.name || !newPlayerData.date_of_birth || !newPlayerData.parent_name || !newPlayerData.parent_email}
              className="w-full"
              data-testid="create-player-submit"
            >
              {submitting ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Plus size={18} className="mr-2" />
              )}
              Create Player & Add to Session
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
